import { useEffect } from 'react';
import { useModal } from '@ebay/nice-modal-react';
import { useQueryClient } from '@tanstack/react-query';
import { usePreservedCallback } from '@toss/react';
import { PlusIcon } from '@zep-us/zus-icons';
import { useRouter } from 'next/router';
import { useTranslation } from 'next-i18next';
import { useAuth } from '@zep/auth';
import { Button, Spacing } from '@zep/ui';
import { PlanKeys } from '../../../../../consts';
import { PostBillingCardCallBack } from '../../../../../services';
import { CreditCardIcon, RotateCircleIcon } from '../../../../icons';
import { InputCardNameModal, makeRedirectUrl, PaypleWidgetDefaultPayload, usePayple } from '../../../subscription';
import { usePlanPaymentFormContext, usePlanPaymentInfoContext } from '../../PlanPaymentSection/Providers';
import { PlanNotiText } from '../PlanDurationSelector/shared';
export const PaypleCardRegisterSection = () => {
  const {
    t
  } = useTranslation(['subscription']);
  const {
    user
  } = useAuth();
  const queryClient = useQueryClient();
  const router = useRouter();
  const {
    spaceHashId,
    planPaymentInfo
  } = usePlanPaymentInfoContext();
  const {
    watch,
    formState
  } = usePlanPaymentFormContext();
  const isCardRegistered = watch('isCardRegistered');
  const planType = watch('planType');
  const maxCcu = watch('maxCcu');
  const subscriptionPeriod = watch('subscriptionPeriod');
  const {
    showWidget,
    isSuccess
  } = usePayple();
  const inputCardNameModal = useModal(InputCardNameModal);
  const showEditPaymentCardModal = () => {
    void showWidget({
      ...PaypleWidgetDefaultPayload,
      type: 'subscription',
      playerEmail: user?.snsId || '',
      spaceHashId,
      callbackUrl: PostBillingCardCallBack,
      failRedirectUrl: makeRedirectUrl(router, {
        isBillingRegister: false,
        plan: planType,
        maxCcu: maxCcu.toString(),
        subscriptionPeriod
      }),
      successRedirectUrl: makeRedirectUrl(router, {
        isBillingRegister: true,
        plan: planType,
        maxCcu: maxCcu.toString(),
        subscriptionPeriod
      })
    });
  };
  const onSuccessRegisterBilling = usePreservedCallback(() => {
    void inputCardNameModal.show({
      spaceHashId,
      onSuccess() {
        void queryClient.invalidateQueries(PlanKeys.spaceDetail(spaceHashId));
        inputCardNameModal.remove();
      }
    });
  });
  useEffect(() => {
    if (isSuccess) {
      onSuccessRegisterBilling();
    }
  }, [isSuccess, onSuccessRegisterBilling]);
  return <>
      {isCardRegistered ? <div className="flex h-[70px] w-full items-center justify-between rounded-[10px] border border-gray-300 bg-white pl-[26px] pr-[24px]">
          <div className="flex items-center gap-2xs">
            <CreditCardIcon width={18} height={18} />
            <span className="text-body-1-sb font-semibold text-gray-900">
              {planPaymentInfo?.billingInfo?.cardName}
            </span>
          </div>
          <Button size="xs" variant="sub" leftSlot={<RotateCircleIcon width={14} height={14} />} onClick={showEditPaymentCardModal}>
            {t('subscription:payment.cardChange.buttonChange', '결제수단 변경')}
          </Button>
        </div> : <div className="flex h-[70px] w-full items-center justify-between rounded-[10px] border border-gray-300 bg-gray-50 pl-[26px] pr-[24px]">
          <div className="flex items-center gap-2xs">
            <CreditCardIcon width={18} height={18} />
            <span className="text-body-1-sb font-semibold text-[#27262E8A]">
              {t('subscription:payment.paymentMethod.cardRegister', '결제 카드를 등록해 주세요')}
            </span>
          </div>
          <Button size="xs" leftSlot={<PlusIcon width={14} height={14} />} onClick={showEditPaymentCardModal}>
            {t('subscription:payment.paymentMethod.cardRegisterButton', '카드 등록')}
          </Button>
        </div>}
      {formState.errors.isCardRegistered && <div className="flex w-full flex-col justify-start">
          <Spacing size={8} />
          <span className="text-body-2-sb font-semibold text-red">
            {t('subscription:payment.paymentMethod.cardRegister', '결제 카드를 등록해 주세요')}
          </span>
        </div>}
      <Spacing size={8} data-sentry-element="Spacing" data-sentry-source-file="PaypleCardRegisterSection.tsx" />
      <div className="flex w-full items-center gap-3xs">
        <PlanNotiText alertText={t('subscription:paymentPayple.cardRegister.description', '구독 결제에 사용되는 카드입니다. 카드 확인을 위해 카드 등록 후 $1가 먼저 결제됩니다. 결제된 $1는 영업일 기준 3 ~ 7일 후 환불됩니다.')} data-sentry-element="PlanNotiText" data-sentry-source-file="PaypleCardRegisterSection.tsx" />
      </div>
    </>;
};